import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { map, switchMap, take } from 'rxjs/operators';
import { of } from 'rxjs';

import { WidgetsService } from '@services/widgets.service';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { CompaniesService } from '@services/companies.service';
import { EntrySummaryModel, EntrySummaryShortModel } from '@data-layer/models/entry-summaries';
import {
  enumEntrySummaryDutiesPaymentType,
  enumEntrySummaryState,
  enumEntrySummarySubmissionType,
} from '@data-layer/enums/entry-summaries';
import { enumPaymentState } from '@data-layer/enums/payments';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { EntrySummaryRepository } from '@data-layer/repositories';
import { enumDropdownActionIconColor, enumDropdownActionMenuMode } from '@shared/form-wrapper/enums';
import { DocumentsService } from '@services/documents.service';
import { enumGATagEventCategory } from '@enum/enumGATagEventCategory';
import { enumGATagEventAction } from '@enum/enumGATagEventAction';

@Component({
  selector: 'es-actions',
  templateUrl: './es-actions.component.html',
  styleUrls: ['./es-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsActionsComponent implements OnChanges {
  @Input() public es: EntrySummaryModel | EntrySummaryShortModel;
  @Input() public state: enumEntrySummaryState;
  @Input() public paymentState: enumPaymentState;
  @Input() public viewEs: boolean = true;
  @Input() public showGoToChat: boolean = false;
  @Input() public showDelivery: boolean = true;
  @Input() public showComplete: boolean = true;
  @Input() public showDuplicate: boolean = true;
  @Input() public showDelete: boolean = true;
  @Input() public showContactSupport: boolean = true;
  @Input() public showPrint: boolean = false;
  @Input() public showInList: boolean;
  @Input() public iconColor: enumDropdownActionIconColor = enumDropdownActionIconColor.LIGHT_GRAY;
  @Output() public update: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public print: EventEmitter<any> = new EventEmitter<any>();

  public stateCode: enumEntrySummaryState;
  public isShowComplete: boolean = true;
  public isShowDuplicate: boolean = true;
  public isShowGoToChat: boolean = true;
  public isShowDelete: boolean = true;
  public isShowContactSupport: boolean = false;
  public completeText: string;
  public openDeleteModal: boolean = false;
  public showPoaRequiredModal: boolean = false;
  public enumDropdownActionMenuMode: typeof enumDropdownActionMenuMode = enumDropdownActionMenuMode;

  constructor(
    private router: Router,
    private entrySummaryRepository: EntrySummaryRepository,
    private widgetsService: WidgetsService,
    private ga: GoogleAnalyticsService,
    private companiesService: CompaniesService,
    private documentsService: DocumentsService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    this.stateCode = this.state;
    if (changes.state || changes.paymentState) {
      this.initData();
    }
  }

  public printPage() {
    this.print.emit();
  }

  public completeEs() {
    this.router.navigate(['entry-summary', this.es.referenceNumber]);
  }

  public goToChat() {
    this.router.navigate(['entry-summary', this.es.referenceNumber, 'custom-broker']);
  }

  public duplicateEs() {
    this.documentsService
      .canActWithoutPoaPrompt$()
      .pipe(take(1))
      .subscribe((canProceed: boolean) => {
        if (canProceed) {
          this.performDuplicate();
        } else {
          this.showPoaRequiredModal = true;
          this.cdr.markForCheck();
        }
      });
  }

  public onPoaModalDiscard(): void {
    this.showPoaRequiredModal = false;
    this.cdr.markForCheck();
  }

  public onPoaModalGoToPoa(): void {
    const companyId: number = this.companiesService.getCompany()?.id;
    if (!companyId) {
      return;
    }
    this.router.navigate(['/companies', 'account-settings', 'company-view', companyId], {
      queryParams: { isEditViewOpen: true },
      fragment: 'power-of-attorney',
    });
  }

  private performDuplicate(): void {
    this.entrySummaryRepository
      .duplicate(this.es.id)
      .pipe(
        switchMap((res) => {
          return res.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED
            ? this.documentsService.copyEntryPoaToEntry(this.es.id, res.id).pipe(map(() => res))
            : of(res);
        }),
        switchMap((res) => {
          return res.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED &&
            res.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.ACH
            ? this.documentsService.copyEntryPunToEntry(this.es.id, res.id).pipe(map(() => res))
            : of(res);
        }),
        take(1),
      )
      .subscribe((res) => {
        this.ga.emitCustomEvent(
          enumGATagEventCategory.ES,
          enumGATagEventAction.ESDuplicate,
          `${res.referenceNumber} FROM ${this.es.referenceNumber}`,
        );
        this.router.navigate(
          ['entry-summary', res.referenceNumber],
          //#10823
          {
            queryParams: { isDuplicate: true },
          },
        );
      });
  }

  public delete() {
    this.openDeleteModal = false;
    this.entrySummaryRepository
      .delete(this.es.id)
      .pipe(take(1))
      .subscribe(() => {
        this.ga.emitCustomEvent(enumGATagEventCategory.ES, enumGATagEventAction.ESDelete, this.es.referenceNumber);
        this.update.emit(true);
      });
  }

  public contactToSupport() {
    const { referenceNumber } = this.es;
    this.widgetsService.trackData('Contact team:', { referenceNumber });
    this.widgetsService.openWidget(['es']);
  }

  public openModal() {
    const companyPaymentType: enumCompanyPaymentType = this.companiesService.getCompany().customsPaymentType;

    if (
      (companyPaymentType === enumCompanyPaymentType.MONTHLY && ![undefined, null].includes(this.paymentState)) ||
      (companyPaymentType === enumCompanyPaymentType.INSTANT &&
        [enumPaymentState.SUCCESS, enumPaymentState.DELAYED].includes(this.paymentState))
    ) {
      this.openDeleteModal = true;
    } else {
      this.delete();
    }
  }

  public onDecline(): void {
    this.openDeleteModal = false;
  }

  public goToDeliveryOrder(): void {
    this.router.navigate(['entry-summary', this.es.referenceNumber, 'delivery-order']);
  }

  private initData() {
    this.getCompleteText();
    this.getIsShowDuplicate();
  }

  private getCompleteText() {
    switch (this.paymentState) {
      case enumPaymentState.NOT_VALIDATED:
      case enumPaymentState.CANCELED:
      case enumPaymentState.FAILED:
        this.completeText = 'COMPLETE_PAYMENT';
        break;
      default:
        this.completeText = 'EDIT';
        break;
    }

    if (!!this.completeText) {
      this.completeText = `BUTTON.${this.completeText}`;
    }
  }

  private getIsShowDuplicate() {
    this.isShowGoToChat =
      (this.es as EntrySummaryModel).submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED && this.showGoToChat;

    if (this.es.statement) {
      this.isShowDelete = false;
      return;
    }

    switch (this.es.state) {
      case enumEntrySummaryState.NOT_SENT:
      case enumEntrySummaryState.DUTY_CALCULATION_COMPLETE:
      case enumEntrySummaryState.NO_BILL_ON_FILE:
        this.isShowDuplicate = this.showDuplicate;
        this.isShowDelete = this.showDelete && this.showDeleteByPaymentState(this.paymentState);
        this.isShowContactSupport = false;
        break;
      case enumEntrySummaryState.APPROVED:
      case enumEntrySummaryState.BROKER_PROCESSING_FAILED:
        this.isShowDuplicate = this.showDuplicate;
        this.isShowDelete =
          this.showDelete &&
          this.es.submissionType === enumEntrySummarySubmissionType.MANUAL &&
          this.showDeleteByPaymentState(this.paymentState);
        this.isShowContactSupport = false;
        break;
      case enumEntrySummaryState.UNRECOVERABLE:
        this.isShowDuplicate = this.showDuplicate;
        this.isShowContactSupport = this.showContactSupport;
        this.isShowDelete = false;
        break;
      case enumEntrySummaryState.REJECTED:
      case enumEntrySummaryState.NOT_VALID_PROVIDER:
        this.isShowDuplicate = this.showDuplicate;
        this.isShowContactSupport = this.showContactSupport;
        this.isShowDelete = this.showDelete && this.showDeleteByPaymentState(this.paymentState);
        break;
      case enumEntrySummaryState.DUTY_CALCULATION_PENDING:
      case enumEntrySummaryState.SENT_TO_CBP:
      case enumEntrySummaryState.PROVIDER_SEND_PENDING:
      case enumEntrySummaryState.ACCEPTED:
      case enumEntrySummaryState.BOND_PENDING:
      case enumEntrySummaryState.SENT_TO_PROVIDER:
      case enumEntrySummaryState.VALIDATED_AT_PROVIDER:
      // #13112 — duplicate allowed in broker-review (state 50); mirrors BROKER_PROCESSING_PENDING (120).
      case enumEntrySummaryState.BROKER_REVIEW_PENDING:
      case enumEntrySummaryState.BROKER_PROCESSING_PENDING:
      case enumEntrySummaryState.BROKER_PROCESSING_FINISHING:
      case enumEntrySummaryState.BROKER_PROCESSING_CARGO_RELEASE_PENDING:
      case enumEntrySummaryState.AWAITING_ARRIVAL:
        this.isShowDuplicate = this.showDuplicate;
        this.isShowDelete = false;
        this.isShowContactSupport = false;
        break;
      case enumEntrySummaryState.CBP_DELETE_PENDING:
      case enumEntrySummaryState.CBP_DELETED:
      case enumEntrySummaryState.DELETED:
        this.isShowDuplicate = false;
        this.isShowDelete = false;
        this.isShowContactSupport = false;
        break;
      default:
        this.isShowDuplicate = false;
        this.isShowDelete = false;
        this.isShowContactSupport = false;
        break;
    }
  }

  private showDeleteByPaymentState(paymentState: enumPaymentState) {
    return ![enumPaymentState.CREATED, enumPaymentState.CONFIRMED, enumPaymentState.PENDING].includes(paymentState);
  }
}
